// Admin Cmd+K command palette — global search + quick actions
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { Search, User2, ShoppingBag, Package, FileText, Building2, Sparkles, Plus, Boxes, Receipt } from "lucide-react";
import { api } from "../api/client";

const TYPE_ICON = {
    user: User2,
    order: ShoppingBag,
    product: Package,
    prescription: FileText,
    b2b_account: Building2,
};

const QUICK_ACTIONS = [
    { id: "promo", label: "Create promo code", to: "/admin/promos", icon: Plus, hint: "P" },
    { id: "rx-next", label: "Next prescription to review", to: "/admin/prescriptions", icon: FileText, hint: "R" },
    { id: "forecast", label: "Open inventory forecast", to: "/admin/inventory/forecast", icon: Sparkles, hint: "F" },
    { id: "po-new", label: "New purchase order", to: "/admin/purchase-orders", icon: Boxes, hint: "O" },
    { id: "b2b-orders", label: "B2B orders queue", to: "/admin/b2b/orders", icon: Receipt, hint: "B" },
];

const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const navigate = useNavigate();
    const debouncedRef = useRef(null);

    // Keyboard listener — Cmd+K / Ctrl+K
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        if (debouncedRef.current) clearTimeout(debouncedRef.current);
        if (!q.trim()) { setResults([]); return; }
        debouncedRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/global-search", { params: { q: q.trim(), limit: 5 } });
                setResults(data.results || []);
            } catch { /* ignore */ }
        }, 200);
    }, [q, open]);

    const go = useCallback((url) => {
        setOpen(false);
        setQ("");
        navigate(url);
    }, [navigate]);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[120] flex items-start justify-center bg-zinc-900/40 p-4 pt-32 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="cmdk-overlay">
            <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-200" onClick={(e) => e.stopPropagation()} data-testid="cmdk-modal">
                <Command label="Admin command palette" shouldFilter={false}>
                    <div className="flex items-center gap-2 border-b border-zinc-100 px-4">
                        <Search className="h-4 w-4 text-zinc-400" />
                        <Command.Input
                            value={q}
                            onValueChange={setQ}
                            placeholder="Search customers, orders, products, prescriptions, B2B accounts…"
                            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-zinc-400"
                            data-testid="cmdk-input"
                        />
                        <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-500">ESC</kbd>
                    </div>
                    <Command.List className="max-h-96 overflow-y-auto py-2">
                        {!q && (
                            <Command.Group heading="Quick actions" className="px-1">
                                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Quick actions</div>
                                {QUICK_ACTIONS.map((a) => {
                                    const Icon = a.icon;
                                    return (
                                        <Command.Item
                                            key={a.id}
                                            value={a.label}
                                            onSelect={() => go(a.to)}
                                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-brand-50 aria-selected:bg-brand-100"
                                            data-testid={`cmdk-action-${a.id}`}
                                        >
                                            <Icon className="h-3.5 w-3.5 text-brand-700" />
                                            <span className="flex-1">{a.label}</span>
                                            <kbd className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[9px] text-zinc-500">{a.hint}</kbd>
                                        </Command.Item>
                                    );
                                })}
                            </Command.Group>
                        )}
                        {q && results.length === 0 && (
                            <Command.Empty className="py-6 text-center text-xs text-zinc-400" data-testid="cmdk-empty">
                                No results for "{q}"
                            </Command.Empty>
                        )}
                        {results.length > 0 && (
                            <div className="px-1">
                                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Results · {results.length}</div>
                                {results.map((r) => {
                                    const Icon = TYPE_ICON[r.type] || Search;
                                    return (
                                        <Command.Item
                                            key={`${r.type}-${r.id}`}
                                            value={`${r.title} ${r.type}`}
                                            onSelect={() => go(r.url)}
                                            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-brand-50 aria-selected:bg-brand-100"
                                            data-testid={`cmdk-result-${r.type}-${r.id}`}
                                        >
                                            <Icon className="h-3.5 w-3.5 text-brand-700" />
                                            <div className="min-w-0 flex-1">
                                                <p className="line-clamp-1 font-medium">{r.title}</p>
                                                <p className="line-clamp-1 text-[11px] text-zinc-500">{r.subtitle}</p>
                                            </div>
                                            <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] uppercase text-zinc-500">{r.type}</span>
                                        </Command.Item>
                                    );
                                })}
                            </div>
                        )}
                    </Command.List>
                </Command>
            </div>
        </div>
    );
};

export default CommandPalette;
